import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { UsersRound, Users, Search } from 'lucide-react';
import { groupAPI } from '../../lib/api';

export default function GroupMembersModal({
  open, onClose, group, students, membersToManage, toggleMember, submitting, onSave, classId
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [unassignedStudents, setUnassignedStudents] = useState([]);

  useEffect(() => {
    if (open && classId) {
      groupAPI.getUnassignedStudents(classId).then(res => {
        setUnassignedStudents(res.data || []);
      }).catch(() => setUnassignedStudents([]));
      setSearchTerm('');
    }
  }, [open, classId]);

  if (!group) return null;

  const currentMembers = group.members || [];
  const filteredCurrentMembers = currentMembers.filter(m =>
    !searchTerm || m.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredUnassigned = unassignedStudents.filter(s =>
    !searchTerm || s.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UsersRound className="w-5 h-5" style={{ color: group.color }} />
            Miembros: {group.name}
          </DialogTitle>
          <DialogDescription>
            Gestiona los estudiantes de este grupo
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="member-search-input"
            />
          </div>

          {/* Current Members */}
          {currentMembers.length > 0 && (
            <div>
              <Label className="text-slate-600 mb-2 block">
                Miembros actuales ({currentMembers.length})
              </Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {filteredCurrentMembers.map(member => (
                  <div 
                    key={member.user_id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      membersToManage.includes(member.user_id) 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}
                    onClick={() => toggleMember(member.user_id)}
                  >
                    <Checkbox 
                      checked={membersToManage.includes(member.user_id)}
                      onCheckedChange={() => toggleMember(member.user_id)}
                    />
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {member.photo_url ? (
                        <img src={member.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-medium text-slate-600">
                          {member.user_name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{member.user_name}</p>
                      <p className="text-xs text-slate-500 truncate">{member.user_email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">Miembro</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unassigned Students */}
          <div>
            <Label className="text-slate-600 mb-2 block">
              Estudiantes sin grupo ({filteredUnassigned.length})
            </Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filteredUnassigned.map(student => (
                <div 
                  key={student.user_id}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                    membersToManage.includes(student.user_id) 
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                  onClick={() => toggleMember(student.user_id)}
                >
                  <Checkbox 
                    checked={membersToManage.includes(student.user_id)}
                    onCheckedChange={() => toggleMember(student.user_id)}
                  />
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {student.photo_url ? (
                      <img src={student.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-medium text-slate-600">
                        {student.user_name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{student.user_name}</p>
                    <p className="text-xs text-slate-500 truncate">{student.user_email}</p>
                  </div>
                </div>
              ))}
              
              {filteredUnassigned.length === 0 && (
                <div className="text-center py-4 text-slate-500">
                  <Users className="w-6 h-6 mx-auto mb-1 opacity-50" />
                  <p className="text-sm">
                    {searchTerm ? 'No se encontraron resultados' : 'Todos los estudiantes están asignados a un grupo'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSave} disabled={submitting}>
            {submitting ? 'Guardando...' : 'Guardar Miembros'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
